/**
 * ChartPanel v2 — trading-grade chart for Indian equities.
 *
 * Features
 * --------
 *   Chart types:          candle | line | area | heikin-ashi
 *   Timeframes:           1D / 1W / 1M (weekly / monthly resampled server-side)
 *   Ranges:               1M / 3M / 6M / 1Y / 3Y / 5Y
 *   Price-scale:          linear / log toggle
 *   Overlays (main pane): SMA, EMA, Bollinger bands, Supertrend, VWAP
 *   Oscillators:          RSI, MACD, Stochastic, ADX, ATR, OBV (each in its own pane)
 *   Compare overlay:      superimpose another symbol's normalised series
 *   Fullscreen:           expand to viewport
 *   Persistence:          localStorage per-ISIN, stores active indicators /
 *                         chart-type / timeframe / log scale
 *
 * Data flow
 * ---------
 *   OHLCV      → GET /api/india/ohlc/{isin}?range=...
 *   Indicators → GET /api/india/indicators/{isin}?indicators=...&range=...&interval=...
 *
 * Each oscillator is its own lightweight-charts instance stacked under the
 * main chart — this is what TradingView recommends for multi-pane layouts
 * until lightweight-charts v5 officially lands panes.
 */

import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import axios from 'axios';
import { createChart, ColorType } from 'lightweight-charts';
import {
  Loader2, AlertCircle, Maximize2, Minimize2, Plus, X, TrendingUp, Settings,
  Minus, Square, Type, ArrowUpRight, Undo2, Trash2, Share2,
} from 'lucide-react';

import { attachDrawingLayer, encodeLayout, decodeLayout } from './drawingLayer';

const INDIA_API = `${process.env.REACT_APP_BACKEND_URL}/api/india`;

// ── Configuration ─────────────────────────────────────────

const RANGES = ['1M', '3M', '6M', '1Y', '3Y', '5Y'];
const INTERVALS = [
  { value: '1d', label: '1D' },
  { value: '1w', label: '1W' },
  { value: '1mo', label: '1M' },
];
const CHART_TYPES = [
  { value: 'candle', label: 'Candle' },
  { value: 'line',   label: 'Line' },
  { value: 'area',   label: 'Area' },
  { value: 'ha',     label: 'HA' },
];

// every overlay / oscillator the user can toggle on.
const INDICATOR_CATALOG = [
  // Overlays (drawn on the main pane)
  { spec: 'sma(50)',         label: 'SMA 50',       color: '#3b82f6', kind: 'overlay' },
  { spec: 'sma(200)',        label: 'SMA 200',      color: '#a855f7', kind: 'overlay' },
  { spec: 'ema(21)',         label: 'EMA 21',       color: '#10b981', kind: 'overlay' },
  { spec: 'ema(50)',         label: 'EMA 50',       color: '#0ea5e9', kind: 'overlay' },
  { spec: 'bollinger(20,2)', label: 'Bollinger 20', color: '#f59e0b', kind: 'overlay' },
  { spec: 'supertrend(10,3)', label: 'Supertrend',  color: '#ef4444', kind: 'overlay' },
  { spec: 'vwap',            label: 'VWAP',         color: '#eab308', kind: 'overlay' },
  // Oscillators (their own pane)
  { spec: 'rsi(14)',         label: 'RSI 14',       color: '#a855f7', kind: 'oscillator' },
  { spec: 'macd(12,26,9)',   label: 'MACD',         color: '#10b981', kind: 'oscillator' },
  { spec: 'stochastic(14,3,3)', label: 'Stochastic', color: '#0ea5e9', kind: 'oscillator' },
  { spec: 'adx(14)',         label: 'ADX 14',       color: '#f59e0b', kind: 'oscillator' },
  { spec: 'atr(14)',         label: 'ATR 14',       color: '#94a3b8', kind: 'oscillator' },
  { spec: 'obv',             label: 'OBV',          color: '#22d3ee', kind: 'oscillator' },
];

const COLORS = {
  up: '#10b981',
  down: '#ef4444',
  volUp: 'rgba(16, 185, 129, 0.5)',
  volDown: 'rgba(239, 68, 68, 0.5)',
  grid: 'rgba(100, 116, 139, 0.12)',
  border: 'rgba(100, 116, 139, 0.3)',
  text: '#94a3b8',
  crosshair: '#10b981',
  compare: '#f59e0b',
};

// ── Persistence helpers ──────────────────────────────────

const STORE_KEY = (isin) => `kubair:chart:${isin}`;

const loadPrefs = (isin) => {
  try {
    const raw = localStorage.getItem(STORE_KEY(isin));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const savePrefs = (isin, prefs) => {
  try {
    localStorage.setItem(STORE_KEY(isin), JSON.stringify(prefs));
  } catch { /* quota? ignore */ }
};

// ── Data transforms ───────────────────────────────────────

const toUnix = (d) => Math.floor(new Date(d).getTime() / 1000);

const toCandleSeries = (candles) => candles
  .map((c) => ({
    time: toUnix(c.date),
    open: Number(c.open), high: Number(c.high),
    low: Number(c.low),   close: Number(c.close),
  }))
  .filter((c) => Number.isFinite(c.open) && Number.isFinite(c.close));

const toLineSeries = (candles) => candles
  .map((c) => ({ time: toUnix(c.date), value: Number(c.close) }))
  .filter((c) => Number.isFinite(c.value));

const toHeikinAshi = (candles) => {
  const out = [];
  let prevHA = null;
  for (const c of candles) {
    const o = Number(c.open), h = Number(c.high), l = Number(c.low), cl = Number(c.close);
    if (![o, h, l, cl].every(Number.isFinite)) continue;
    const haClose = (o + h + l + cl) / 4;
    const haOpen = prevHA ? (prevHA.open + prevHA.close) / 2 : (o + cl) / 2;
    const haHigh = Math.max(h, haOpen, haClose);
    const haLow  = Math.min(l, haOpen, haClose);
    const bar = { time: toUnix(c.date), open: haOpen, high: haHigh, low: haLow, close: haClose };
    out.push(bar);
    prevHA = bar;
  }
  return out;
};

const toVolume = (candles) => candles.map((c) => ({
  time: toUnix(c.date),
  value: Number(c.volume) || 0,
  color: Number(c.close) >= Number(c.open) ? COLORS.volUp : COLORS.volDown,
}));

const pointsToSeries = (points) => (points || [])
  .filter((p) => p && p.value !== null && p.value !== undefined)
  .map((p) => ({ time: toUnix(p.time), value: Number(p.value) }));

// Normalise a close series to the first value (for compare overlay)
const normalise = (candles) => {
  const valid = candles.map((c) => Number(c.close)).filter(Number.isFinite);
  if (!valid.length) return [];
  const first = valid[0];
  return candles
    .map((c) => {
      const v = Number(c.close);
      if (!Number.isFinite(v)) return null;
      return { time: toUnix(c.date), value: (v / first - 1) * 100 };
    })
    .filter(Boolean);
};

// ── Main component ────────────────────────────────────────

export default function ChartPanel({ isin, height = 460, resource = 'stock', compareKey = '' }) {
  // `resource === 'index'` swaps the data endpoints from stock-OHLC to
  // index-OHLC, so this same component powers the IndexTAPage.
  const baseOhlc = resource === 'index'
    ? `${INDIA_API}/index-ohlc`
    : `${INDIA_API}/ohlc`;
  const baseIndicators = resource === 'index'
    ? `${INDIA_API}/index-indicators`
    : `${INDIA_API}/indicators`;
  // Persistable user prefs
  const defaults = useMemo(() => ({
    range: '1Y',
    interval: '1d',
    chartType: 'candle',
    log: false,
    indicators: ['sma(50)', 'sma(200)', 'rsi(14)'],
    compareSymbol: '',
  }), []);
  const [prefs, setPrefs] = useState(() => ({ ...defaults, ...(loadPrefs(isin) || {}) }));
  const [showIndicatorPicker, setShowIndicatorPicker] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    if (!isin) return;
    savePrefs(isin, prefs);
  }, [isin, prefs]);

  // Data
  const [candles, setCandles] = useState([]);
  const [compareCandles, setCompareCandles] = useState([]);
  const [indicators, setIndicators] = useState([]);   // computed series
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Drawings — persisted via /api/india/chart-layouts/{isin}.
  const [drawings, setDrawings] = useState([]);
  const [activeTool, setActiveTool] = useState(null); // trendline | hline | ...
  const drawingLayerRef = useRef(null);
  const skipNextSave = useRef(false);

  // Load drawings from backend on mount, then from URL hash if present.
  useEffect(() => {
    if (!isin) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${INDIA_API}/chart-layouts/${isin}`);
        if (cancelled) return;
        const fromUrl = (window.location.hash || '').replace(/^#layout=/, '');
        const decoded = fromUrl ? decodeLayout(fromUrl) : null;
        skipNextSave.current = true;
        setDrawings((decoded && decoded.drawings) || (data && data.layout && data.layout.drawings) || []);
      } catch {
        // not logged in / no network — just start empty
      }
    })();
    return () => { cancelled = true; };
  }, [isin]);

  // Autosave drawings (debounced).
  useEffect(() => {
    if (!isin) return;
    if (skipNextSave.current) { skipNextSave.current = false; return; }
    const t = setTimeout(() => {
      axios
        .put(`${INDIA_API}/chart-layouts/${isin}`, { layout: { drawings } })
        .catch(() => { /* silent */ });
    }, 700);
    return () => clearTimeout(t);
  }, [isin, drawings]);

  // Fetch OHLC + indicators whenever isin / range / interval / indicators change.
  useEffect(() => {
    if (!isin) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: ohlc } = await axios.get(`${baseOhlc}/${isin}`, {
          params: { range: prefs.range },
        });
        if (cancelled) return;
        const bars = (ohlc && ohlc.candles) || [];
        setCandles(prefs.interval === '1d' ? bars : resampleClientSide(bars, prefs.interval));

        if (prefs.indicators.length > 0) {
          const { data: ind } = await axios.get(`${baseIndicators}/${isin}`, {
            params: {
              indicators: prefs.indicators.join(','),
              range: prefs.range,
              interval: prefs.interval,
            },
          });
          if (!cancelled) setIndicators(ind.indicators || []);
        } else {
          setIndicators([]);
        }

        // Compare overlay always uses the stock base (comparing an index to a
        // stock doesn't make sense; even indices compare to other indices via
        // their own URL which clients can compose). For simplicity we keep it
        // on the same base as the main chart.
        if (prefs.compareSymbol) {
          try {
            const { data: c } = await axios.get(`${baseOhlc}/${prefs.compareSymbol}`, {
              params: { range: prefs.range },
            });
            if (!cancelled) setCompareCandles(c.candles || []);
          } catch {
            if (!cancelled) setCompareCandles([]);
          }
        } else {
          setCompareCandles([]);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message || 'Failed to load chart');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isin, prefs.range, prefs.interval, prefs.indicators, prefs.compareSymbol, baseOhlc, baseIndicators]);

  // Group indicators by pane.
  const overlays = useMemo(
    () => (indicators || []).filter((i) => i.kind === 'overlay'),
    [indicators],
  );
  const oscillators = useMemo(
    () => (indicators || []).filter((i) => i.kind === 'oscillator'),
    [indicators],
  );

  // Toggle an indicator on/off.
  const toggleIndicator = (spec) => {
    setPrefs((p) => {
      const has = p.indicators.includes(spec);
      return { ...p, indicators: has ? p.indicators.filter((x) => x !== spec) : [...p.indicators, spec] };
    });
  };

  // Panel shell
  const shell = (
    <div className={fullscreen
      ? 'fixed inset-0 z-[900] bg-background p-4 overflow-auto'
      : 'space-y-3'}
    >
      {/* ─── Top controls row ─── */}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Range chips */}
          <div className="flex gap-0.5 p-0.5 bg-muted/40 rounded-md">
            {RANGES.map((r) => (
              <button
                key={r}
                onClick={() => setPrefs((p) => ({ ...p, range: r }))}
                className={`px-2 py-0.5 text-[11px] font-semibold rounded transition-colors ${
                  prefs.range === r
                    ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {r}
              </button>
            ))}
          </div>
          {/* Interval */}
          <div className="flex gap-0.5 p-0.5 bg-muted/40 rounded-md">
            {INTERVALS.map((t) => (
              <button
                key={t.value}
                onClick={() => setPrefs((p) => ({ ...p, interval: t.value }))}
                className={`px-2 py-0.5 text-[11px] font-semibold rounded transition-colors ${
                  prefs.interval === t.value
                    ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {/* Chart type */}
          <div className="flex gap-0.5 p-0.5 bg-muted/40 rounded-md">
            {CHART_TYPES.map((c) => (
              <button
                key={c.value}
                onClick={() => setPrefs((p) => ({ ...p, chartType: c.value }))}
                className={`px-2 py-0.5 text-[11px] font-semibold rounded transition-colors ${
                  prefs.chartType === c.value
                    ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                title={c.label}
              >
                {c.label}
              </button>
            ))}
          </div>
          {/* Log / linear */}
          <button
            onClick={() => setPrefs((p) => ({ ...p, log: !p.log }))}
            className={`px-2 py-0.5 text-[11px] font-semibold rounded-md border transition-colors ${
              prefs.log
                ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                : 'border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            LOG
          </button>
        </div>

        <div className="flex items-center gap-2">
          <CompareInput
            value={prefs.compareSymbol}
            onCommit={(v) => setPrefs((p) => ({ ...p, compareSymbol: v.trim().toUpperCase() }))}
          />
          <button
            onClick={() => setShowIndicatorPicker((s) => !s)}
            className="flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-md border border-border hover:bg-muted"
            title="Add / remove indicators"
          >
            <Plus className="w-3 h-3" /> Indicators
          </button>
          <button
            onClick={() => setFullscreen((f) => !f)}
            className="p-1 rounded-md border border-border hover:bg-muted"
            title="Fullscreen"
          >
            {fullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Drawing toolbar */}
      <DrawingToolbar
        activeTool={activeTool}
        onToolChange={setActiveTool}
        onClear={() => setDrawings([])}
        onUndo={() => setDrawings((d) => d.slice(0, -1))}
        onShare={() => {
          const h = `#layout=${encodeLayout({ drawings })}`;
          const url = `${window.location.origin}${window.location.pathname}${window.location.search}${h}`;
          if (navigator.clipboard) navigator.clipboard.writeText(url).catch(() => { /* ignore */ });
          window.history.replaceState(null, '', h);
        }}
      />

      {/* Active indicator chips */}
      {prefs.indicators.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {prefs.indicators.map((spec) => {
            const meta = INDICATOR_CATALOG.find((i) => i.spec === spec);
            if (!meta) return null;
            return (
              <span
                key={spec}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border border-border bg-card"
                style={{ color: meta.color, borderColor: `${meta.color}66` }}
              >
                {meta.label}
                <button onClick={() => toggleIndicator(spec)} className="opacity-60 hover:opacity-100">
                  <X className="w-2.5 h-2.5" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Indicator picker */}
      {showIndicatorPicker && (
        <IndicatorPicker
          active={prefs.indicators}
          onToggle={toggleIndicator}
          onClose={() => setShowIndicatorPicker(false)}
        />
      )}

      {/* Status line */}
      <div className="text-[10px] text-muted-foreground h-4">
        {loading && <span className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</span>}
        {!loading && error && <span className="flex items-center gap-1 text-red-500"><AlertCircle className="w-3 h-3" /> {error}</span>}
        {!loading && !error && candles.length > 0 && (
          <span>{candles.length} bars · {prefs.interval.toUpperCase()} · {prefs.range}{prefs.compareSymbol ? ` · vs ${prefs.compareSymbol}` : ''}</span>
        )}
      </div>

      {/* Main chart pane */}
      <MainChart
        candles={candles}
        compareCandles={compareCandles}
        overlays={overlays}
        chartType={prefs.chartType}
        log={prefs.log}
        height={fullscreen ? Math.max(500, window.innerHeight - 400) : height}
        drawings={drawings}
        activeTool={activeTool}
        onDrawingsChange={setDrawings}
        drawingLayerRef={drawingLayerRef}
      />

      {/* Oscillator panes */}
      {oscillators.map((osc) => (
        <OscillatorChart
          key={osc.name}
          osc={osc}
          height={fullscreen ? 180 : 120}
        />
      ))}
    </div>
  );

  return shell;
}

// ── Compare symbol input ──────────────────────────────────

function CompareInput({ value, onCommit }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <div className="flex items-center gap-1 border border-border rounded-md px-2 py-0.5 bg-card">
      <TrendingUp className="w-3 h-3 text-muted-foreground" />
      <input
        placeholder="Compare (eg. TCS)"
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => onCommit(v)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onCommit(v); } }}
        className="w-24 bg-transparent text-[11px] focus:outline-none uppercase"
      />
      {v && (
        <button onClick={() => { setV(''); onCommit(''); }} className="text-muted-foreground hover:text-foreground">
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

// ── Indicator picker panel ────────────────────────────────

function IndicatorPicker({ active, onToggle, onClose }) {
  return (
    <div className="rounded-lg border border-border bg-card/70 backdrop-blur p-3 space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold flex items-center gap-1"><Settings className="w-3 h-3" /> Indicators</h4>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      {['overlay', 'oscillator'].map((kind) => (
        <div key={kind}>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
            {kind === 'overlay' ? 'Overlays (price pane)' : 'Oscillators (separate pane)'}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
            {INDICATOR_CATALOG.filter((i) => i.kind === kind).map((i) => {
              const on = active.includes(i.spec);
              return (
                <button
                  key={i.spec}
                  onClick={() => onToggle(i.spec)}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] border transition-colors ${
                    on
                      ? 'border-emerald-500/60 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                      : 'border-border text-muted-foreground hover:text-foreground'
                  }`}
                  style={on ? { color: i.color, borderColor: `${i.color}aa` } : undefined}
                >
                  <span className="w-2 h-2 rounded-full" style={{ background: i.color }} />
                  {i.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main (price + volume + overlays) pane ─────────────────

function MainChart({
  candles, compareCandles, overlays, chartType, log, height,
  drawings = [], activeTool = null, onDrawingsChange, drawingLayerRef,
}) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const mainSeriesRef = useRef(null);
  const volSeriesRef = useRef(null);
  const overlayRefs = useRef({}); // spec -> series (or [series, ...])
  const compareSeriesRef = useRef(null);
  const drawLayerRef = useRef(null);

  // Build/rebuild the chart when dimensions change, log toggles, or chartType changes.
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: COLORS.text,
        fontFamily: 'Inter, ui-sans-serif, system-ui',
        fontSize: 11,
      },
      rightPriceScale: { borderColor: COLORS.border, mode: log ? 1 : 0 },
      timeScale: { borderColor: COLORS.border, timeVisible: false, rightOffset: 6 },
      grid: {
        vertLines: { color: COLORS.grid },
        horzLines: { color: COLORS.grid },
      },
      crosshair: {
        vertLine: { color: COLORS.crosshair, width: 1, style: 3, labelBackgroundColor: COLORS.crosshair },
        horzLine: { color: COLORS.crosshair, width: 1, style: 3, labelBackgroundColor: COLORS.crosshair },
      },
      autoSize: true,
    });

    let mainSeries;
    if (chartType === 'line') {
      mainSeries = chart.addLineSeries({ color: COLORS.up, lineWidth: 2 });
    } else if (chartType === 'area') {
      mainSeries = chart.addAreaSeries({
        lineColor: COLORS.up, topColor: 'rgba(16, 185, 129, 0.35)', bottomColor: 'rgba(16, 185, 129, 0.02)',
        lineWidth: 2,
      });
    } else {
      mainSeries = chart.addCandlestickSeries({
        upColor: COLORS.up, downColor: COLORS.down,
        wickUpColor: COLORS.up, wickDownColor: COLORS.down,
        borderVisible: false,
      });
    }

    const vol = chart.addHistogramSeries({
      color: COLORS.volUp,
      priceFormat: { type: 'volume' },
      priceScaleId: '',
    });
    vol.priceScale().applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
    mainSeries.priceScale().applyOptions({ scaleMargins: { top: 0.05, bottom: 0.22 } });

    chartRef.current = chart;
    mainSeriesRef.current = mainSeries;
    volSeriesRef.current = vol;

    const ro = new ResizeObserver(() => {
      chart.applyOptions({
        width: containerRef.current?.clientWidth || 0,
      });
    });
    ro.observe(containerRef.current);

    // Attach drawing overlay — synchronised to this chart
    drawLayerRef.current = attachDrawingLayer({
      container: containerRef.current,
      chart,
      mainSeries,
      drawings,
      onChange: (list) => { if (typeof onDrawingsChange === 'function') onDrawingsChange(list); },
    });
    if (drawingLayerRef) drawingLayerRef.current = drawLayerRef.current;

    return () => {
      try { ro.disconnect(); } catch { /* ignore */ }
      try { drawLayerRef.current && drawLayerRef.current.destroy(); } catch { /* ignore */ }
      try { chart.remove(); } catch { /* ignore */ }
      chartRef.current = null;
      mainSeriesRef.current = null;
      volSeriesRef.current = null;
      overlayRefs.current = {};
      compareSeriesRef.current = null;
      drawLayerRef.current = null;
    };
  }, [chartType, log]);

  // Sync tool + drawings into the layer as props change.
  useEffect(() => {
    if (drawLayerRef.current) drawLayerRef.current.setTool(activeTool);
  }, [activeTool]);
  useEffect(() => {
    if (drawLayerRef.current) drawLayerRef.current.setDrawings(drawings || []);
  }, [drawings]);

  // Push candle / volume / overlays into the chart.
  useEffect(() => {
    const chart = chartRef.current;
    const main = mainSeriesRef.current;
    const vol = volSeriesRef.current;
    if (!chart || !main || !vol) return;

    // Price series
    if (chartType === 'line' || chartType === 'area') {
      main.setData(toLineSeries(candles));
    } else if (chartType === 'ha') {
      main.setData(toHeikinAshi(candles));
    } else {
      main.setData(toCandleSeries(candles));
    }
    vol.setData(toVolume(candles));

    // Compare overlay (separate scale)
    if (compareSeriesRef.current) {
      try { chart.removeSeries(compareSeriesRef.current); } catch { /* ignore */ }
      compareSeriesRef.current = null;
    }
    if (compareCandles && compareCandles.length > 0) {
      const s = chart.addLineSeries({
        color: COLORS.compare,
        lineWidth: 2,
        lineStyle: 2,
        priceScaleId: 'compare',
      });
      s.priceScale().applyOptions({ scaleMargins: { top: 0.05, bottom: 0.22 } });
      s.setData(normalise(compareCandles));
      compareSeriesRef.current = s;
    }

    // Overlays
    // Clear old overlay series cleanly.
    Object.values(overlayRefs.current).forEach((handle) => {
      const list = Array.isArray(handle) ? handle : [handle];
      list.forEach((s) => { try { chart.removeSeries(s); } catch { /* ignore */ } });
    });
    overlayRefs.current = {};

    overlays.forEach((o) => {
      const spec = INDICATOR_CATALOG.find((i) => o.name.startsWith(i.spec.split('(')[0]));
      const color = spec?.color || '#10b981';

      if (o.data) {
        // Single-line overlay (SMA, EMA, VWAP)
        const s = chart.addLineSeries({ color, lineWidth: 1.5, priceLineVisible: false, lastValueVisible: false });
        s.setData(pointsToSeries(o.data));
        overlayRefs.current[o.name] = s;
      } else if (o.series) {
        if (o.series.upper && o.series.lower && o.series.middle) {
          // Bollinger: three lines
          const up = chart.addLineSeries({ color: `${color}cc`, lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
          const mid = chart.addLineSeries({ color: `${color}66`, lineWidth: 1, lineStyle: 2, priceLineVisible: false, lastValueVisible: false });
          const lo = chart.addLineSeries({ color: `${color}cc`, lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
          up.setData(pointsToSeries(o.series.upper));
          mid.setData(pointsToSeries(o.series.middle));
          lo.setData(pointsToSeries(o.series.lower));
          overlayRefs.current[o.name] = [up, mid, lo];
        } else if (o.series.line && o.series.direction) {
          // Supertrend: line coloured by direction
          const s = chart.addLineSeries({ color, lineWidth: 2, priceLineVisible: false, lastValueVisible: false });
          s.setData(pointsToSeries(o.series.line));
          overlayRefs.current[o.name] = s;
        }
      } else if (o.levels) {
        // Pivot lines (horizontal price lines on the main series)
        const added = [];
        for (const [name, px] of Object.entries(o.levels)) {
          if (!Number.isFinite(Number(px))) continue;
          added.push(main.createPriceLine({
            price: Number(px),
            color: `${color}88`,
            lineWidth: 1,
            lineStyle: 2,
            axisLabelVisible: true,
            title: name.toUpperCase(),
          }));
        }
        overlayRefs.current[o.name] = added;
      }
    });

    if (candles.length) chartRef.current.timeScale().fitContent();
  }, [candles, compareCandles, overlays, chartType]);

  return (
    <div
      ref={containerRef}
      className="relative w-full rounded-lg border border-border/50 bg-card"
      style={{ height }}
    />
  );
}

// ── Oscillator pane ───────────────────────────────────────

function OscillatorChart({ osc, height }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: COLORS.text,
        fontFamily: 'Inter, ui-sans-serif, system-ui',
        fontSize: 10,
      },
      rightPriceScale: { borderColor: COLORS.border, scaleMargins: { top: 0.1, bottom: 0.1 } },
      timeScale: { borderColor: COLORS.border, timeVisible: false, visible: false },
      grid: { vertLines: { color: COLORS.grid }, horzLines: { color: COLORS.grid } },
      crosshair: {
        vertLine: { color: COLORS.crosshair, width: 1, style: 3 },
        horzLine: { color: COLORS.crosshair, width: 1, style: 3 },
      },
      autoSize: true,
    });
    chartRef.current = chart;

    const nameRoot = osc.name.split('_')[0];
    const color = (INDICATOR_CATALOG.find((i) => i.spec.startsWith(nameRoot))?.color) || '#10b981';

    if (osc.data) {
      const s = chart.addLineSeries({ color, lineWidth: 2 });
      s.setData(pointsToSeries(osc.data));
      if (nameRoot === 'rsi') {
        [30, 70].forEach((lvl) => s.createPriceLine({
          price: lvl, color: `${color}66`, lineStyle: 2, lineWidth: 1,
          axisLabelVisible: true, title: String(lvl),
        }));
      }
    } else if (osc.series) {
      if (osc.series.line && osc.series.signal && osc.series.hist) {
        // MACD — line + signal + histogram
        const line = chart.addLineSeries({ color: COLORS.up, lineWidth: 2 });
        const signal = chart.addLineSeries({ color: '#f59e0b', lineWidth: 1 });
        const hist = chart.addHistogramSeries({ color: `${COLORS.up}99` });
        line.setData(pointsToSeries(osc.series.line));
        signal.setData(pointsToSeries(osc.series.signal));
        const histPts = pointsToSeries(osc.series.hist).map((p) => ({
          ...p,
          color: p.value >= 0 ? 'rgba(16,185,129,0.55)' : 'rgba(239,68,68,0.55)',
        }));
        hist.setData(histPts);
      } else if (osc.series.k && osc.series.d) {
        // Stochastic — %K + %D + 20/80 threshold lines
        const k = chart.addLineSeries({ color: COLORS.up, lineWidth: 1.5 });
        const d = chart.addLineSeries({ color: '#f59e0b', lineWidth: 1 });
        k.setData(pointsToSeries(osc.series.k));
        d.setData(pointsToSeries(osc.series.d));
        [20, 80].forEach((lvl) => k.createPriceLine({
          price: lvl, color: '#94a3b866', lineStyle: 2, lineWidth: 1,
          axisLabelVisible: true, title: String(lvl),
        }));
      } else if (osc.series.adx && osc.series.plus_di && osc.series.minus_di) {
        // ADX — adx + +DI + -DI
        const a = chart.addLineSeries({ color: color, lineWidth: 2 });
        const p = chart.addLineSeries({ color: COLORS.up, lineWidth: 1 });
        const m = chart.addLineSeries({ color: COLORS.down, lineWidth: 1 });
        a.setData(pointsToSeries(osc.series.adx));
        p.setData(pointsToSeries(osc.series.plus_di));
        m.setData(pointsToSeries(osc.series.minus_di));
      }
    }

    const ro = new ResizeObserver(() => {
      chart.applyOptions({ width: containerRef.current?.clientWidth || 0 });
    });
    ro.observe(containerRef.current);
    chart.timeScale().fitContent();

    return () => {
      try { ro.disconnect(); } catch { /* ignore */ }
      try { chart.remove(); } catch { /* ignore */ }
      chartRef.current = null;
    };
  }, [osc]);

  const nameRoot = osc.name.split('_')[0].toUpperCase();

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between px-1">
        <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
          {nameRoot}
        </span>
      </div>
      <div
        ref={containerRef}
        className="w-full rounded-lg border border-border/50 bg-card"
        style={{ height }}
      />
    </div>
  );
}

// ── Drawing toolbar ───────────────────────────────────────

function DrawingToolbar({ activeTool, onToolChange, onClear, onUndo, onShare }) {
  const tools = [
    { id: 'trendline', icon: ArrowUpRight, label: 'Trendline' },
    { id: 'hline',     icon: Minus,        label: 'Horizontal' },
    { id: 'vline',     icon: () => <span className="text-[10px] font-mono">|</span>, label: 'Vertical' },
    { id: 'rect',      icon: Square,       label: 'Rectangle' },
    { id: 'fib',       icon: () => <span className="text-[9px] font-mono font-bold">FIB</span>, label: 'Fibonacci' },
    { id: 'text',      icon: Type,         label: 'Note' },
  ];

  return (
    <div className="flex flex-wrap items-center gap-1">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mr-1">Draw:</div>
      {tools.map((t) => {
        const Icon = t.icon;
        const active = activeTool === t.id;
        return (
          <button
            key={t.id}
            title={t.label}
            onClick={() => onToolChange(active ? null : t.id)}
            className={`p-1.5 rounded-md border transition-colors ${
              active
                ? 'border-emerald-500/60 bg-emerald-500/10 text-emerald-500'
                : 'border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
          </button>
        );
      })}
      <div className="w-px h-5 bg-border mx-1" />
      <button
        onClick={onUndo}
        title="Undo last drawing"
        className="p-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground"
      >
        <Undo2 className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={onClear}
        title="Clear all drawings"
        className="p-1.5 rounded-md border border-border text-muted-foreground hover:text-red-500"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={onShare}
        title="Copy shareable chart URL"
        className="p-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground"
      >
        <Share2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}


// ── Client-side fallback resampler (in case backend skips) ──

function resampleClientSide(bars, interval) {
  if (!bars || interval === '1d') return bars;
  const buckets = {};
  const order = [];
  for (const b of bars) {
    const d = new Date(b.date);
    let key;
    if (interval === '1w') {
      const onejan = new Date(d.getFullYear(), 0, 1);
      const week = Math.ceil(((d - onejan) / 86400000 + onejan.getDay() + 1) / 7);
      key = `${d.getFullYear()}-W${String(week).padStart(2, '0')}`;
    } else {
      key = `${d.getFullYear()}-M${String(d.getMonth() + 1).padStart(2, '0')}`;
    }
    if (!buckets[key]) {
      buckets[key] = { ...b };
      order.push(key);
      continue;
    }
    const cur = buckets[key];
    cur.date = b.date;
    cur.high = Math.max(Number(cur.high) || 0, Number(b.high) || 0);
    cur.low = Math.min(Number(cur.low) || Infinity, Number(b.low) || Infinity);
    cur.close = b.close;
    cur.volume = (Number(cur.volume) || 0) + (Number(b.volume) || 0);
  }
  return order.map((k) => buckets[k]);
}
